<template>
  <div class="legal-document-container max-w-4xl mx-auto pb-6 px-6 bg-white shadow-lg rounded-xl">
    
    <div class="top-0 z-20 bg-white pt-6 pb-4 border-b border-gray-100">
      <div class="flex justify-center">
        <div class="inline-flex p-1 bg-gray-100 rounded-lg">
          <button 
            @click="activeLang = 'de'"
            :class="['px-6 py-2 text-sm font-bold rounded-md transition-all', activeLang === 'de' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700']"
          >
            Deutsch
          </button>
          <button 
            @click="activeLang = 'en'"
            :class="['px-6 py-2 text-sm font-bold rounded-md transition-all', activeLang === 'en' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700']"
          >
            English
          </button>
        </div>
      </div>
    </div>

    <section v-for="(entries, date) in sortedGroups" :key="date" class="mb-12">
      
      <div class="top-[73px] z-10 bg-white py-4 border-b-2 border-gray-100 mb-6">
        <div class="flex items-center">
          <span class="ml-4 px-2 py-1 bg-blue-50 text-blue-600 text-[10px] rounded uppercase font-bold tracking-widest">
            Version
          </span>
          <h2 class="text-xl font-bold text-blue-900">{{ formatDate(date) }}</h2>
        </div>
      </div>

      <div class="space-y-12">
        <div v-for="entry in entries" :key="entry.id" class="document-section">
          
          <div class="flex items-center gap-2 mb-4">
            <span class="text-sm font-bold text-gray-700 uppercase tracking-wide">
              {{ entry.document_level_description }}
            </span>

          </div>

          <div v-if="activeLang === 'de'" class="animate-fadeIn">
            <div 
              class="prose prose-blue max-w-none text-gray-800" 
              v-html="renderMarkdown(entry.text_de_markdown || entry.text_de)"
            ></div>
          </div>

          <div v-if="activeLang === 'en'" class="animate-fadeIn">
            <div v-if="entry.text_en || entry.text_en_markdown">
              <div 
                class="prose prose-blue max-w-none text-gray-800"
                v-html="renderMarkdown(entry.text_en_markdown || entry.text_en)"
              ></div>
            </div>
            <div v-else class="flex items-center justify-center p-8 bg-gray-50 border border-dashed rounded-lg text-gray-400 italic text-sm">
              Keine englische Übersetzung vorhanden / No English translation available.
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { marked } from 'marked';

marked.setOptions({
  breaks: true,
  gfm: true
});

const props = defineProps({
  standardTexts: {
    type: Array,
    required: true
  }
});

const activeLang = ref('de');

// Logic to group the flat array into an object keyed by date
const sortedGroups = computed(() => {
  if (!props.standardTexts) return {};
  return props.standardTexts.reduce((acc, obj) => {
    const key = obj.version_date || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(obj);
    return acc;
  }, {});
});

const renderMarkdown = (text) => {
  if (!text) return '';
  return marked.parse(text);
};

const formatDate = (dateStr) => {
  if (dateStr === 'Unknown') return dateStr;
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit', month: 'long', year: 'numeric'
  });
};
</script>

<style scoped>
.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Typography styles for the rendered Markdown */
:deep(.prose h2) {
  @apply text-lg font-extrabold text-gray-900 border-b border-gray-100 pb-1 mb-4;
}
:deep(.prose p) {
  @apply leading-relaxed mb-4;
}
</style>